import { z } from "zod";
import { brandingSchema, localizedSchema } from "./schemas";
import type { CustomerSettingsView } from "@/stores/customerStore";

export const settingsSchema = z
  .object({
    name: localizedSchema,
    website: z.string().url(),
    branding: brandingSchema,
    notifications: z
      .object({
        leadEmail: z.union([z.literal(""), z.email()]),
        emailAlerts: z.boolean(),
        dailyDigest: z.boolean(),
        handoffAlerts: z.boolean(),
      })
      .strict(),
  })
  .strict();
export type EditableSettings = z.infer<typeof settingsSchema>;
export function editableSettings(view: CustomerSettingsView): EditableSettings {
  return {
    name: view.profile.name,
    website: view.profile.website,
    branding: view.branding,
    notifications: { ...view.notifications, leadEmail: view.notifications.leadEmail ?? "" },
  };
}
export type SettingsChange = {
  document: "business" | "customer";
  path: string[];
  before: string | boolean | undefined;
  after: string | boolean;
};
export function settingsChanges(
  before: EditableSettings,
  after: EditableSettings,
): SettingsChange[] {
  settingsSchema.parse(after);
  if (Object.keys(before.name).some((key) => !Object.hasOwn(after.name, key)))
    throw new Error("Business name translations cannot be removed here.");
  const changes: SettingsChange[] = [];
  const add = (
    document: SettingsChange["document"],
    path: string[],
    original: SettingsChange["before"],
    value: SettingsChange["after"],
  ) => {
    if (original !== value) changes.push({ document, path, before: original, after: value });
  };
  for (const [lang, value] of Object.entries(after.name))
    add("business", ["name", lang], before.name[lang], value);
  add("business", ["website"], before.website, after.website);
  for (const key of Object.keys(after.branding) as (keyof EditableSettings["branding"])[])
    add("business", ["branding", key], before.branding[key], after.branding[key]);
  for (const key of Object.keys(after.notifications) as (keyof EditableSettings["notifications"])[])
    add(
      "customer",
      ["notificationPreferences", key],
      before.notifications[key],
      after.notifications[key],
    );
  return changes;
}
/** Refresh untouched fields from subscriptions while retaining edits and their original conflict baseline. */
export function mergeSettings(
  current: EditableSettings,
  baseline: EditableSettings,
  edited: EditableSettings,
): EditableSettings {
  return {
    name: {
      ...current.name,
      ...Object.fromEntries(
        Object.entries(edited.name).filter(([key, value]) => baseline.name[key] !== value),
      ),
    },
    website: edited.website !== baseline.website ? edited.website : current.website,
    branding: Object.fromEntries(
      Object.entries(current.branding).map(([key, value]) => [
        key,
        edited.branding[key as keyof typeof edited.branding] !==
        baseline.branding[key as keyof typeof baseline.branding]
          ? edited.branding[key as keyof typeof edited.branding]
          : value,
      ]),
    ) as EditableSettings["branding"],
    notifications: Object.fromEntries(
      Object.entries(current.notifications).map(([key, value]) => [
        key,
        edited.notifications[key as keyof typeof edited.notifications] !==
        baseline.notifications[key as keyof typeof baseline.notifications]
          ? edited.notifications[key as keyof typeof edited.notifications]
          : value,
      ]),
    ) as EditableSettings["notifications"],
  };
}
